import {
  ColumnDef,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Quotations } from './queryOptions';
import { TableCell, Typography } from '@maia/ui';
import { match } from 'ts-pattern';
import { formatCurrency } from '@maia/utils';
import { useState } from 'react';

type Quotation = Quotations['response'][number];

const columns: ColumnDef<Quotation>[] = [
  {
    accessorKey: 'reference',
    header: 'Nummer',
    cell: ({ row }) => {
      return match(row.depth)
        .with(0, () => (
          <TableCell.Link
            to="/quotations/$guid"
            params={{
              guid: row.original.guid,
            }}
          >
            {row.original.reference}
          </TableCell.Link>
        ))

        .otherwise(() => null);
    },
    enableSorting: false,
  },
  {
    accessorKey: 'createdAt',
    header: 'Datum',
    cell: ({ row }) => {
      return match(row.depth)
        .with(0, () => <TableCell.Date date={row.original.createdAt} />)
        .otherwise(() => null);
    },
    enableSorting: false,
  },
  {
    accessorKey: 'products',
    header: 'Producten',
    enableSorting: false,
    cell: ({ row }) => {
      return match({ canExpand: row.getCanExpand(), depth: row.depth })
        .with({ canExpand: true, depth: 0 }, () => (
          <TableCell.Dropdown
            title="Meerdere producten"
            onPress={() => row.toggleExpanded()}
          />
        ))
        .with({ canExpand: false, depth: 1 }, () => (
          <TableCell.DropdownProduct
            icon={row.original.products?.[0].icon}
            title={row.original.products?.[0].title}
            price={formatCurrency(
              row.original.products?.[0].price.premium_after_tax,
            )}
          />
        ))
        .otherwise(() => (
          <Typography variant="body-xs" weight="medium">
            {row.original.products?.[0].title}
          </Typography>
        ));
    },
  },
  {
    accessorKey: 'price',
    header: 'Bedrag',
    cell: ({ row }) => {
      return match(row.depth)
        .with(0, () => <TableCell.Price price={row.original.price} />)
        .otherwise(() => null);
    },
    enableSorting: false,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      return match(row.depth)
        .with(0, () => <TableCell.Tag {...row.original.status} />)
        .otherwise(() => null);
    },
    enableSorting: false,
  },
  //   TODO: Add back
  //   {
  //     id: 'actions',
  //     cell: ({ row }) => {
  //       return match(row.depth)
  //         .with(0, () => (
  //           <div className="flex gap-x-12 items-center">
  //             <ActionButton icon={IconAngleRight} size="small" />
  //           </div>
  //         ))
  //         .otherwise(() => null);
  //     },
  //   },
];

export function useQuotationsTable(data?: Quotations) {
  const [expanded, setExpanded] = useState<ExpandedState>({});

  return useReactTable({
    columns,
    data: data?.response ?? [],
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => {
      if (row.products.length > 1) {
        return row.products.map((product) => ({ ...row, products: [product] }));
      }

      return undefined;
    },
    rowCount: data?.response.length,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: data?.pagination.totalPages,
  });
}
